import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { applyWriteMap } from "../firebase/writes.js";
import {
  type CustomStunt,
  type CustomizationRecord,
  addLibraryStunt,
  removeLibraryStunt,
} from "../lib/customization-edits.js";

interface Props {
  tablePath: string;
  customization: CustomizationRecord;
}

const COST_TYPES: CustomStunt["cost"]["type"][] = [
  "passive",
  "fp",
  "per_scene",
  "per_session",
  "conditional",
];

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export default function CustomizationStuntsEditor({
  tablePath,
  customization,
}: Props): ReactElement {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [costType, setCostType] = useState<CustomStunt["cost"]["type"]>("passive");

  async function write(writes: Record<string, unknown>): Promise<void> {
    const { db } = getFirebase();
    await applyWriteMap(db, writes);
  }

  async function handleAdd(e: FormEvent): Promise<void> {
    e.preventDefault();
    const id = slug(name);
    if (!id) return;
    await write(
      addLibraryStunt(tablePath, customization, {
        id,
        nameKey: name.trim(),
        descKey: desc.trim(),
        cost: { type: costType },
      }),
    );
    setName("");
    setDesc("");
    setCostType("passive");
  }

  const stunts = customization.stunts.addedLibrary ?? [];

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold uppercase text-muted-foreground">
        {t("customization.stunts.library")}
      </h3>
      <ul className="space-y-1 text-sm">
        {stunts.map((s) => (
          <li key={s.id} className="flex items-center gap-2">
            <span className="flex-1">
              <span className="font-medium">{s.nameKey}</span>{" "}
              <span className="text-xs text-muted-foreground">· {s.cost.type}</span>
            </span>
            <button
              type="button"
              className="rounded border border-rose-400 px-2 py-0.5 text-xs text-rose-700"
              onClick={() => write(removeLibraryStunt(tablePath, customization, s.id))}
            >
              {t("edit.delete")}
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={handleAdd} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 text-sm">
        <input
          className="rounded border px-2 py-1"
          placeholder={t("customization.stunts.name-placeholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label={t("customization.stunts.new-name")}
        />
        <input
          className="rounded border px-2 py-1"
          placeholder={t("customization.stunts.desc-placeholder")}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          aria-label={t("customization.stunts.new-desc")}
        />
        <select
          className="rounded border px-2 py-1"
          value={costType}
          onChange={(e) => setCostType(e.target.value as CustomStunt["cost"]["type"])}
          aria-label={t("customization.stunts.new-cost")}
        >
          {COST_TYPES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <button type="submit" className="rounded border px-2 py-1">
          {t("edit.save")}
        </button>
      </form>
    </section>
  );
}
